import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NavigationRouteLink } from '@app/model/navigation-route-link';
import { PayableNode } from '@app/model/payable-node';
import SwipeNavigation from '@app/plugins/native/swipe-navigation.plugin';
import { Capacitor } from '@capacitor/core';
import { BundleType } from 'library-explorer';
import { BrowserNavigationService } from './browser-navigation/browser-navigation.service';
import { SettingsService } from './settings.service';

@Injectable({
  providedIn: 'root'
})
export class NavigateService {
  prevPageUrl: string;
  currentPageUrl: string;
  prevPageScrollPos: [number, number];

  private skipModulesLevel = false;

  constructor(
    private readonly browserNavigationService: BrowserNavigationService,
    private readonly settingsService: SettingsService,
    private readonly router: Router) {
    this.init();
  }

  public navigateByNavigationRouteLink(link: NavigationRouteLink): void {
    if (!link) {
      return;
    }

    switch (link.route) {
      case 'page.class':
        this.router.navigate(['/courses'], {
          queryParams: { classID: link.routeParams?.classId },
          state: { autoOpen: false }
        });
        break;
      case 'page.course':
        this.router.navigate(['/modules'], {
          queryParams: { courseID: link.routeParams?.courseId },
          state: { autoOpen: false }
        });
        break;
      case 'page.module':
        this.router.navigate(['/lessons'], {
          queryParams: { moduleID: link.routeParams?.moduleId },
          state: { autoOpen: false }
        });
        break;
      case 'page.slides':
        this.router.navigate(['/chapters'], {
          queryParams: { lessonID: link.routeParams?.contentId },
          state: { autoOpen: false }
        });
        break;
      case 'page.webinar':
        this.router.navigate(['/webinars', link.routeParams?.webinarId]);
        break;
      case 'library.file.preview':
        this.router.navigate(['/library'], { queryParams: { openFileById: link.routeParams?.libraryFileId } });
        break;
      case 'page.learning_path':
        this.router.navigate(['/learning-path', link.routeParams?.learningPathId]);
        break;
    }
  }

  public navigate(url: string, target: '_blank' | '_self' = '_blank'): void {
    if (this.hasDomain(url)) {
      this.browserNavigationService.open(url, target);
      return;
    }
    
    const urlFragments = url.split('?');
    const path = urlFragments[0];
    const queryParams = {};
    let queryParamsList = [];
    if (urlFragments[1]) {
      queryParamsList = urlFragments[1].split('&');
      queryParamsList.forEach(item => {
        const paramFragments = item.split('=');
        const key = paramFragments[0];
        const value = paramFragments[1];
        queryParams[key] = value;
      });
    }
    this.router.navigate([path], { queryParams });
  }

  public navigateToLogin(): void {
    this.router.navigateByUrl('/login');
  }

  public navigateToClasses(): void {
    this.router.navigateByUrl('/classes');
  }

  public navigateToLibrary(): void {
    this.router.navigateByUrl('/library');
  }

  public navigateToClass(classID: string): void {
    this.router.navigateByUrl('/courses?classID=' + classID);
  }

  public navigateToCourse(courseID: string): void {
    this.router.navigateByUrl('/modules?courseID=' + courseID);
  }

  public navigateToEntity(node: Partial<PayableNode>): void {
    const { id: entityId, bundle: entityBundle, moduleId } = node || {};

    if (entityBundle === BundleType.COURSE && this.skipModulesLevel && moduleId) {
      this.navigateToEntity({ id: moduleId, bundle: BundleType.MODULE });
      return;
    }

    const routeMap = {
      [BundleType.CLASS]: { path: ['/courses'], queryKey: 'classID', autoOpen: true },
      [BundleType.MODULE]: { path: ['/lessons'], queryKey: 'moduleID', autoOpen: true },
      [BundleType.LESSON]: { path: ['/chapters'], queryKey: 'lessonID', autoOpen: true },
      [BundleType.QUIZ]: { path: ['/chapters'], queryKey: 'lessonID', autoOpen: true },
      [BundleType.COURSE]: { path: ['/modules'], queryKey: 'courseID', autoOpen: true },
      [BundleType.WEBINAR]: { path: ['/webinars', entityId] },
      [BundleType.LIBRARY]: { path: ['/library'], queryKey: 'openFileById' },
    };

    const route = routeMap[entityBundle];

    if (!route) {
      return;
    }

    const queryParams = route.queryKey ? { [route.queryKey]: entityId } : undefined;
    const state = route.autoOpen ? { autoOpen: true } : undefined;

    this.router.navigate(route.path, {
      queryParams,
      state
    });
  }

  public async toggleSwipeNavigation(enable: boolean): Promise<void> {
    if (!this.isNativePlatformAndPluginAvailable()) {
      return;
    }

    const { status: isSwipeEnabled } = await SwipeNavigation.status();

    if (enable && !isSwipeEnabled) {
      await SwipeNavigation.enable();
    } else if (!enable && isSwipeEnabled) {
      await SwipeNavigation.disable();
    }
  }


  private isNativePlatformAndPluginAvailable(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios' && Capacitor.isPluginAvailable('SwipeNavigation');
  }

  public hasDomain(url: string): boolean {
    // ignore obvious relative paths
    if (url.startsWith('/')) return false;

    // detect protocol-based URLs
    if (/^(https?:)?\/\//i.test(url)) return true;

    // detect domain-like structure (example.com, sub.domain.com)
    return /^[a-z0-9.-]+\.[a-z]{2,}($|\/)/i.test(url);
  }

  private init(): void {
    this.settingsService.getSettings()
      .subscribe(data => {
        this.skipModulesLevel = data.system.skip_module;
      });
  }
}
